/**
 * Analyzer: Day Performance Headline
 *
 * Emits one of two observations: "excellent accuracy" when win rate >= 70,
 * or "trade selection concern" when win rate < 40 with ≥3 trades. Silent
 * between those bands — the day grade carries middle-band performance.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';

export const key = 'dayPerformance';
export const tier = 'free';

export const analyze = (ctx) => {
  const { day } = ctx;
  if (!day || !day.core) return null;
  // Live-trade gate: skip when no trades have closed today. v7.5.14 contract
  // extended to day-scope; see dayUtils.js for the closed-only core stats.
  if (day.core.closedTotal === 0) return null;

  const { winRate, tradesCount, avgR } = day.core;
  const winRatePct = winRate * 100;

  // High win-rate celebration
  if (winRatePct >= 70 && tradesCount >= 2) {
    return makeObservation({
      id: `${key}:${day.date}:accurate`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.EXECUTION,
      title: 'Excellent accuracy today',
      detail: `${winRatePct.toFixed(1)}% win rate across ${tradesCount} trades. Trade selection was exceptional.`,
      action: "Pin what filtered the setups today — that's what you want to repeat.",
      evidence: { winRate: winRatePct, tradesCount, avgR },
      metric: { label: 'Win Rate', value: winRatePct.toFixed(1), unit: '%' },
      confidence: 0.85,
      priority: 70
    });
  }

  // Low win-rate warning
  if (winRatePct < 40 && tradesCount >= 3) {
    return makeObservation({
      id: `${key}:${day.date}:selection`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.WARNING,
      category: CATEGORY.EXECUTION,
      title: 'Trade selection off today',
      detail: `${winRatePct.toFixed(1)}% win rate across ${tradesCount} trades. Entries lacked edge or conditions didn't match.`,
      action: 'Review entry criteria vs market regime. Were setups A-grade, or did you force borderline entries?',
      evidence: { winRate: winRatePct, tradesCount, avgR },
      metric: { label: 'Win Rate', value: winRatePct.toFixed(1), unit: '%' },
      confidence: 0.8,
      priority: 75
    });
  }

  // R-multiple warnings stand on their own
  if (avgR < 0 && tradesCount >= 2) {
    return makeObservation({
      id: `${key}:${day.date}:negativeR`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.WARNING,
      category: CATEGORY.RISK,
      title: 'Risk-adjusted return was negative',
      detail: `Average of ${avgR.toFixed(2)}R — you risked more than you returned on average.`,
      action: 'Are losers getting full stops while winners come off early? Check R-ratios on your closed trades.',
      evidence: { avgR, tradesCount },
      metric: { label: 'Avg R', value: avgR.toFixed(2), unit: 'R' },
      confidence: 0.8,
      priority: 72
    });
  }

  if (avgR >= 2 && tradesCount >= 2) {
    return makeObservation({
      id: `${key}:${day.date}:strongR`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.RISK,
      title: 'Outstanding risk management',
      detail: `${avgR.toFixed(2)}R average — winners ran, losers got cut on plan.`,
      action: 'This is the shape you want. Note the trail/stop rules you used — make them defaults.',
      evidence: { avgR, tradesCount },
      metric: { label: 'Avg R', value: avgR.toFixed(2), unit: 'R' },
      confidence: 0.85,
      priority: 65
    });
  }

  return null;
};
